'use client'

import * as React from 'react'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { ArrowUpRight, Check, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'

// Sample multi-source UGC wall. Pulls "from" six networks (Instagram,
// TikTok, YouTube, X, LinkedIn, Threads) into one moderated wall so a
// visitor reads the core promise at a glance: many feeds, one brand-safe
// surface. Filterable by network with keyboard-operable chips. Everything
// here is clearly-labelled SAMPLE content (verified Unsplash IDs reused
// from the repo, no 404s); the real connection happens in-app via OAuth.

type Network = 'instagram' | 'tiktok' | 'youtube' | 'x' | 'linkedin' | 'threads'

const NETWORKS: { value: Network; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'x', label: 'X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'threads', label: 'Threads' },
]

const NETWORK_LABEL: Record<Network, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  linkedin: 'LinkedIn',
  threads: 'Threads',
}

type Tile = {
  id: string
  network: Network
  handle: string
  caption: string
  imageId: string
}

// Sample wall. Handles are obviously-fictional placeholders so nothing
// reads as a real person's quote. IDs are verified present in the repo.
const TILES: Tile[] = [
  { id: 't1', network: 'instagram', handle: '@sample.creator', caption: 'Styled the overcoat three ways', imageId: 'photo-1490481651871-ab68de25d43d' },
  { id: 't2', network: 'tiktok', handle: '@sample.unbox', caption: 'Unboxing in real time', imageId: 'photo-1518791841217-8f162f1e1131' },
  { id: 't3', network: 'youtube', handle: '@sample.reviews', caption: 'Full review + try-on', imageId: 'photo-1556228720-195a672e8a03' },
  { id: 't4', network: 'x', handle: '@sample_buyer', caption: 'Arrived faster than I expected', imageId: 'photo-1485125639709-a60c3a500bf1' },
  { id: 't5', network: 'linkedin', handle: 'Sample Buyer', caption: 'Kit our team actually uses', imageId: 'photo-1503376780353-7e6692767b70' },
  { id: 't6', network: 'threads', handle: '@sample.threads', caption: 'Worth the hype, honestly', imageId: 'photo-1469398715555-76331a6c7c9b' },
  { id: 't7', network: 'instagram', handle: '@sample.daily', caption: 'Everyday carry, sorted', imageId: 'photo-1492725764893-90b379c2b6e7' },
  { id: 't8', network: 'tiktok', handle: '@sample.styles', caption: 'GRWM with the new drop', imageId: 'photo-1503454537195-1dcabb73ffb9' },
  { id: 't9', network: 'youtube', handle: '@sample.field', caption: 'Tested it for a month', imageId: 'photo-1495121605193-b116b5b9c5fe' },
  { id: 't10', network: 'instagram', handle: '@sample.fit', caption: 'Fit check before brunch', imageId: 'photo-1483985988355-763728e1935b' },
  { id: 't11', network: 'threads', handle: '@sample.notes', caption: 'Quick thoughts after a week', imageId: 'photo-1469334031218-e382a71b716b' },
  { id: 't12', network: 'x', handle: '@sample_io', caption: 'Tagging the exact colourway', imageId: 'photo-1493612276216-ee3925520721' },
]

const unsplash = (id: string, w = 400, h = 400) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&h=${h}&q=80`

function NetworkGlyph({ network, className }: { network: Network; className?: string }) {
  // Single-letter monogram badge per source. Decorative: the source name
  // is always available to assistive tech via the visible label / sr-only.
  const letter: Record<Network, string> = {
    instagram: 'In',
    tiktok: 'Tk',
    youtube: 'Yt',
    x: 'X',
    linkedin: 'Li',
    threads: 'Th',
  }
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-brand-navy px-1 text-[10px] font-bold leading-none text-white',
        className,
      )}
    >
      {letter[network]}
    </span>
  )
}

export function SocialAggregatorPreview() {
  const [active, setActive] = React.useState<Network | 'all'>('all')
  const chipRefs = React.useRef<Array<HTMLButtonElement | null>>([])

  const order: Array<Network | 'all'> = ['all', ...NETWORKS.map((n) => n.value)]
  const filtered = active === 'all' ? TILES : TILES.filter((t) => t.network === active)

  // Roving-ish keyboard support: arrow keys move focus between chips.
  const onChipKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const dir = e.key === 'ArrowRight' ? 1 : -1
    const next = (index + dir + order.length) % order.length
    chipRefs.current[next]?.focus()
  }

  return (
    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
      {/* Toolbar: moderated-wall framing + network filter */}
      <div className="flex flex-col gap-4 border-b bg-secondary/40 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
              Moderated wall
            </span>
            <span className="rounded-full border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              Sample data
            </span>
          </div>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            Showing {filtered.length} of {TILES.length} posts
            {active !== 'all' ? ` from ${NETWORK_LABEL[active]}` : ' across 6 networks'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter the wall by network">
          {order.map((value, i) => {
            const isActive = active === value
            const label = value === 'all' ? 'All networks' : NETWORK_LABEL[value]
            return (
              <button
                key={value}
                ref={(el) => {
                  chipRefs.current[i] = el
                }}
                type="button"
                onClick={() => setActive(value)}
                onKeyDown={(e) => onChipKeyDown(e, i)}
                aria-pressed={isActive}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {value !== 'all' && <NetworkGlyph network={value} className={isActive ? 'bg-primary-foreground/15' : ''} />}
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* The wall */}
      {filtered.length > 0 && (
      <ul className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3 lg:grid-cols-4">
        {filtered.map((tile) => (
          <li key={tile.id} className="group relative bg-card">
            <div className="relative aspect-square overflow-hidden bg-secondary">
              <ImgWithFallback
                src={unsplash(tile.imageId)}
                alt={`Sample ${NETWORK_LABEL[tile.network]} post: ${tile.caption}`}
                fill
                sizes="(min-width: 1024px) 22vw, (min-width: 640px) 30vw, 45vw"
                className="object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]"
              />
              <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-card/95 px-1.5 py-0.5 text-[10px] font-semibold text-foreground shadow-sm backdrop-blur">
                <NetworkGlyph network={tile.network} />
                <span className="sr-only">From </span>
                {NETWORK_LABEL[tile.network]}
              </span>
            </div>
            <div className="p-3">
              <p className="truncate text-xs font-semibold text-foreground">{tile.handle}</p>
              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{tile.caption}</p>
            </div>
          </li>
        ))}
      </ul>
      )}

      {filtered.length === 0 && (
        <p className="p-10 text-center text-sm text-muted-foreground">
          No sample posts for that network. Pick another, or connect the real source in the app.
        </p>
      )}

      {/* Honest footer + CTAs */}
      <div className="flex flex-col gap-3 border-t bg-secondary/40 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
          Sample content for layout only. Connect your real accounts in-app via a quick OAuth.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href="/tools/widget-builder?source=instagram"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            Build your wall
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </a>
          <a
            href={appSignupUrl({ source: 'instagram', ref: 'tool-social-aggregator' })}
            className="inline-flex items-center rounded-lg border px-3.5 py-2 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Connect real sources
          </a>
        </div>
      </div>
    </div>
  )
}
